import * as React from 'react';
import { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { MAX_UPLOAD_SIZE_MB } from '../constants/upload';

export interface ImageUploadData {
  path: string;
  filename: string;
  imageData: string;
}

export interface ImageUploadResponse {
  imageUrl: string;
}

export interface ImageUploadButtonProps {
  /** Callback with the uploaded image URL */
  onUpload: (url: string) => void;
  /** Folder path in blob storage (e.g., "pickems/assets/abc123") */
  path: string;
  /** Filename prefix (e.g., "option-image" → "option-image-1234567890.png") */
  filename: string;
  /** Async function to handle the actual upload */
  uploadFn: (data: ImageUploadData) => Promise<ImageUploadResponse>;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Max file size in MB (default: 5) */
  maxSizeMB?: number;
  /** Tooltip/title for the button */
  title?: string;
  /** Additional class names */
  className?: string;
  /** Accepted file types (default: jpeg, png, gif, webp) */
  acceptedTypes?: string[];
  /** Callback for errors */
  onError?: (message: string) => void;
}

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const MIME_TYPE_LABELS: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
};

function formatAcceptedTypesLabel(types: string[]): string {
  const labels = Array.from(
    new Set(types.map(t => MIME_TYPE_LABELS[t] ?? t.replace(/^image\//, '').toUpperCase()))
  );
  if (labels.length <= 1) return labels.join('');
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

export function ImageUploadButton({
  onUpload,
  path,
  filename,
  uploadFn,
  disabled = false,
  maxSizeMB = MAX_UPLOAD_SIZE_MB,
  title = 'Upload custom image',
  className,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  onError,
}: ImageUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleError = (message: string) => {
    if (onError) {
      onError(message);
    } else {
      toast.error(message);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be selected again
    event.target.value = '';

    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      handleError(`Invalid file type. Please select a ${formatAcceptedTypesLabel(acceptedTypes)} image.`);
      return;
    }

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      handleError(`File size exceeds ${maxSizeMB}MB limit.`);
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64Data = reader.result as string;

      setIsUploading(true);
      try {
        const result = await uploadFn({
          path,
          filename,
          imageData: base64Data,
        });
        onUpload(result.imageUrl);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to upload image';
        handleError(message);
      } finally {
        setIsUploading(false);
      }
    };
    reader.onerror = () => {
      handleError('Failed to read file.');
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileChange}
        className="hidden"
        aria-hidden="true"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={disabled || isUploading}
        title={title}
        className={cn('shrink-0', className)}
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Upload className="h-4 w-4" />
        )}
      </Button>
    </>
  );
}
